<script setup>
import { XMarkIcon } from '@heroicons/vue/24/outline'

const tech = useTech()
const now = new Date()
const config = useRuntimeConfig()
const route = useRoute()

const { data } = await useFetch(config.public.BANNER_JSON_URL, {
  query: { affiliate: 'certificates_nuxt' },
  getCachedData (key) {
    return useNuxtData(key).data.value
  },
  timeout: 5000,
  onResponseError () {
    return { data: [] }
  }
})

const bannerDismissed = useCookie('is-bb-banner-dismissed')

const { hasPurchased, isGuest } = useUserPurchaseStatus()

const phase = data.value?.flat()
  .map((item) => ({
    ...item,
    dateString: item.ends,
    remaining: new Date(item.ends) - now,
    prelude: stylize(item.prelude),
    subtitle: stylize(item.subtitle),
    homePrelude: stylize(item.homePrelude),
    header: stylize(item.header),
    homeHeader: stylize(item.homeHeader),
    title: stylize(item.title),
    addendumBefore: stylize(item.addendumBefore)
  }))
  .find(item => item.remaining > 0)

function stylize (string) {
  return string?.replace('[', '<span class="bg-gradient-to-r from-nuxt-green to-nuxt-green bg-clip-text text-transparent">').replace(']', '</span>')
}

const isBannerClosed = ref(true)

const closeBanner = () => {
  isBannerClosed.value = true
  localStorage.setItem('banner', false)
}

onMounted(() => {
  const banner = localStorage?.getItem('banner')

  if (banner === null) {
    isBannerClosed.value = false
  } else {
    isBannerClosed.value = true
  }
})

const showBannerOnLanding = computed(() => {
  if (route?.path.includes('/nuxt') && !route?.path?.includes('/blog')) {
    return true
  } else {
    return phase.showOnHomepage || false
  }
})

const replaceWords = (string, word) => {
  return string?.replace(word.toLowerCase(), '<span class="md:hidden !text-white">NuxtLabs</span><span class="hidden md:inline"><img src="/images/nuxtlabs-logo.svg" class="h-4 md:h-6 w-auto translate-y-[2px]" /></span>')
}

const currentTheme = computed(() => {
  return phase?.theme || 1
})

const allowBannerOnPage = inject('allowBanners', true)

const { AnalyticsEvent, track } = useAnalytics()
function trackPricingClick () {
  track(AnalyticsEvent.VIEW_PRICING, {
    tech: tech.id,
    via: 'banner'
  })
}
</script>

<template>
  <BannerRepublic
    v-if="!isGuest && hasPurchased && tech.id === 'nuxt' && !bannerDismissed && allowBannerOnPage"
    dismissible
    @dismiss="bannerDismissed = true" />
  <NuxtLink
    v-show="!isBannerClosed && (isGuest || !hasPurchased) && phase && showBannerOnLanding && allowBannerOnPage"
    to="/nuxt#pricing"
    :target="route?.path === '/' ? '_blank' : '_self'"
    class="dark group flex h-14 w-full justify-center bg-[#022F34] md:h-12"
    @click="trackPricingClick">
    <div
      v-if="currentTheme === 'conf'"
      class="flex items-center justify-center gap-2 md:gap-8 xl:gap-10">
      <div
        class="hidden items-center gap-2 md:flex md:gap-4">
        <InlineSvg
          src="/images/icon-fire.svg"
          class="h-4 w-auto text-nuxt-green md:h-5"
          alt="fire" />
        <div
          class="w-12 text-center text-xs !font-bold text-nuxt-green md:block md:w-auto md:text-sm xl:text-base"
          v-html="phase?.prelude" />
      </div>
      <div class="mr-4 flex flex-row items-center gap-1 sm:mr-0 md:flex-row md:gap-5">
        <div class="flex w-[200px] flex-col flex-wrap items-center justify-center gap-x-1 md:w-auto md:flex-nowrap lg:flex-row lg:gap-8">
          <div
            class="hidden text-[10px] font-bold text-nuxt-green"
            v-html="phase?.prelude" />
          <div
            v-if="phase?.header"
            class=" items-center gap-4 text-center text-[10px] font-semibold  text-white md:w-auto md:whitespace-nowrap md:text-sm xl:text-base">
            <span v-html="phase.header" />
          </div>
          <div
            v-if="phase?.title"
            class="items-center gap-4 text-center text-[10px] font-semibold text-white md:w-auto md:whitespace-nowrap md:text-sm md:!leading-[1.2rem] xl:text-base [&>span>span]:text-nuxt-green">
            <span v-html="phase.title" />
          </div>
          <NuxtImg
            src="/images/banner-sebastien.png"
            class="ml-5 hidden h-[36px] rounded-full" />
          <div
            v-if="phase?.subtitle"
            class="text-center text-[10px] font-medium text-white md:text-sm xl:text-base min-[1600px]:block [&>b]:font-bold [&>span]:!font-semibold"
            v-html="phase?.subtitle" />
          <div
            v-if="phase?.interlude"
            class="[&>span]:text-next-green block text-center text-[10px] font-bold text-white md:text-base md:leading-none lg:hidden xl:text-base [&>span]:font-bold"
            v-html="phase?.interlude" />
        </div>
        <div
          v-if="phase?.interlude"
          class="hidden text-center text-[10px] font-bold text-white md:text-base md:leading-none lg:block xl:text-base [&>span]:font-bold [&>span]:text-nuxt-green lg:[&>span]:ml-2"
          v-html="phase?.interlude" />
        <div class="flex flex-col items-center gap-0 md:flex-row md:gap-2 lg:gap-0">
          <p
            v-if="phase?.preAddendum"
            class="rounded-md text-center text-[8px] font-bold uppercase text-white md:text-[10px] lg:bg-nuxt-green/20 lg:px-4 lg:py-1">
            {{ phase?.preAddendum }}
          </p>
          <div
            v-if="phase?.addendum"
            class="w-12 text-center text-[8px] font-bold text-white lg:w-auto  lg:px-4 lg:py-1 lg:text-xs"
            style=""
            v-html="phase?.addendum" />
        </div>
        <div
          v-if="phase?.showCountdown"
          class="flex flex-col items-center md:flex-row md:gap-4">
          <span
            v-if="phase?.id?.includes('EXT')"
            class="whitespace-nowrap text-[8px] font-bold uppercase text-nuxt-green md:text-[12px] md:tracking-widest xl:mr-9">
            EXTENDED
          </span>
          <span class="text-gradient-primary whitespace-nowrap text-[8px] font-bold uppercase md:text-[9px] md:tracking-widest">
            {{ phase?.countdownFooter }}
          </span>
          <AppCountdown
            v-bind="{ remaining: phase?.remaining, daysToHours: phase?.showDaysAsHours }"
            class="dark"
            box-class="text-white lg:scale-[1.1] lg:translate-x-2 md:px-2 md:py-1 md:bg-nuxt-green/30" />
        </div>
      </div>
    </div>
    <div
      v-if="currentTheme === 'bf'"
      class="flex items-center justify-center gap-2 md:gap-5 xl:gap-10">
      <div
        class="hidden items-center gap-2 md:flex md:gap-4">
        <div
          class=" flex w-28 flex-col whitespace-pre-line  !text-center text-[12px] font-bold leading-none text-white after:ml-1 after:text-white after:content-['Latecomer'] md:w-auto md:flex-row md:text-base lg:after:ml-8 [&>span]:whitespace-nowrap"
          v-html="phase?.prelude" />
      </div>
      <div class="flex flex-row items-center gap-2 md:flex-row md:gap-5">
        <div class="flex flex-col items-center xl:flex-row xl:gap-8">
          <div
            v-if="phase?.prelude"
            class="text-[11px] font-bold text-white after:ml-1 after:text-white after:content-['Latecomer'] md:hidden xl:text-base [&>span]:whitespace-nowrap"
            v-html="phase?.prelude" />
          <div
            v-if="phase?.header || phase?.homeHeader"
            class="w-auto items-center gap-4 text-center text-[11px] font-semibold leading-none text-white md:w-auto md:whitespace-nowrap md:text-sm xl:text-base [&>span>span]:text-nuxt-green">
            <span v-html="!route?.path.includes('/nuxt') ? stylize(phase?.header) : stylize(phase?.homeHeader || phase?.header)" />
          </div>
          <NuxtImg
            src="/images/banner-sebastien.png"
            class="ml-7 hidden h-[36px] rounded-full xl:block" />
          <div
            v-if="phase?.subtitle"
            class="hidden text-center text-[8px] font-normal text-white md:text-xs xl:block xl:text-base"
            v-html="phase?.subtitle" />
        </div>
        <div class="flex flex-col items-center gap-0 md:flex-row md:gap-2 lg:gap-0">
          <p
            v-if="phase?.preAddendum"
            class="rounded-md text-center text-[8px] font-bold uppercase text-white md:text-[10px] lg:bg-nuxt-green/20 lg:px-4 lg:py-1">
            {{ phase?.preAddendum }}
          </p>
          <div
            v-if="phase?.addendum"
            class="w-10 text-center text-[8px] font-bold uppercase text-white md:w-auto md:px-4 md:py-1 xl:text-xs"
            style="letter-spacing: 1.2px">
            {{ phase?.addendum }}
          </div>
        </div>
        <div
          v-if="phase?.showCountdown"
          class="flex flex-col items-center md:flex-row md:gap-4">
          <span
            v-if="phase?.isExtended"
            class="whitespace-nowrap text-[8px] font-bold uppercase text-nuxt-green md:text-[12px] md:tracking-widest xl:mr-9">
            EXTENDED
          </span>
          <span class="text-gradient-primary whitespace-nowrap text-[8px] font-bold uppercase md:text-[9px] md:tracking-widest">
            {{ phase?.countdownFooter }}
          </span>
          <AppCountdown
            v-bind="{ remaining: phase?.remaining, daysToHours: phase?.showDaysAsHours }"
            class="dark"
            box-class="text-white lg:scale-[1.2] lg:translate-x-2 md:px-2 md:py-1 md:bg-nuxt-green/30" />
        </div>
      </div>
    </div>
    <div
      v-if="currentTheme === 1"
      class="flex items-center justify-center gap-5 md:w-auto md:gap-10 xl:gap-10 2xl:gap-16">
      <div
        v-if="phase?.prelude"
        class="flex items-center gap-2 md:flex md:gap-4">
        <div
          class=" w-12 !text-center text-[10px] font-bold leading-none text-azure-radiance md:block md:w-auto md:text-base"
          v-html="phase?.prelude" />
      </div>

      <div class="flex items-center gap-3 md:gap-4 xl:gap-8 2xl:gap-14">
        <div class="flex max-w-[200px] shrink-0 flex-col flex-wrap items-center justify-center gap-x-2 md:w-auto md:max-w-fit md:shrink md:flex-nowrap lg:flex-row lg:gap-8">
          <div
            v-if="phase?.title"
            class="text-center text-[11px] font-semibold leading-none text-white md:whitespace-nowrap md:text-base md:!leading-[1.2rem] xl:text-base xl:leading-[1.2rem] [&>span]:text-azure-radiance"
            v-html="phase?.title" />
          <div
            v-if="phase?.subtitle"
            class="block text-center text-[10px] font-normal text-white md:text-base xl:inline xl:text-base"
            v-html="phase?.subtitle" />
          <div
            v-if="phase?.interlude"
            class="block text-center text-[10px] font-normal text-white md:hidden md:text-base xl:text-base [&>span]:font-bold [&>span]:text-azure-radiance"
            v-html="phase?.interlude" />
        </div>
        <div
          v-if="phase?.interlude"
          class="hidden text-center text-[10px] font-normal text-white md:block md:text-base xl:inline xl:text-base [&>span]:font-bold [&>span]:text-azure-radiance"
          v-html="phase?.interlude" />

        <div
          v-if="phase?.showCountdown"
          class="flex flex-col items-center xl:flex-row xl:gap-4">
          <span
            class="whitespace-nowrap text-[8px] font-bold uppercase text-white md:text-xs md:tracking-widest">
            {{ phase?.countdownFooter }}
          </span>
          <AppCountdown
            v-bind="{
              remaining: phase?.remaining,
              daysToHours: phase?.showDaysAsHours,
            }"
            class="dark"
            box-class="text-white p-1 md:px-2 md:py-1 bg-azure-radiance/20"
            box-style="" />
        </div>

        <div
          v-if="phase?.addendum"
          class="w-10 text-center text-[8px] font-bold uppercase text-white md:w-auto md:px-4 md:py-1 xl:text-xs"
          style="letter-spacing: 1.2px">
          {{ phase?.addendum }}
        </div>
      </div>
    </div>
    <div
      v-if="currentTheme === 2"
      class="flex w-full items-center justify-center gap-4 px-4 md:gap-5 md:px-6 xl:gap-10">
      <div
        class="flex w-14 items-center gap-2 md:flex md:w-auto md:gap-4">
        <InlineSvg
          hard-code
          src="/images/icon-fire.svg"
          class="h-4 w-auto shrink-0 text-nuxt-green md:h-6" />
        <div
          class="block text-xs font-bold text-nuxt-green md:block xl:text-base"
          v-html="$route?.path === '/' ? phase?.prelude : phase?.prelude" />
      </div>

      <div class="flex flex-row items-center gap-4 md:flex-row md:gap-5 xl:gap-8">
        <div class="flex flex-col items-center gap-0 whitespace-nowrap md:flex-row md:gap-4 xl:gap-8">
          <!-- <div
          v-if="phase?.prelude || phase?.homePrelude"
          class="w-[90px] text-center text-[10px] font-bold text-nuxt-green md:hidden md:w-auto lg:whitespace-nowrap xl:text-base"
          v-html="$route?.path === '/' ? phase?.prelude : phase?.homePrelude" /> -->
          <div
            v-if="phase?.header || phase?.homeHeader"
            class="banner-text text-center text-[10px] font-semibold text-white md:block md:w-auto md:text-xs lg:whitespace-nowrap  xl:text-base"
            v-html="$route?.path === '/' ? phase?.header : phase?.homeHeader || phase?.header" />

          <div
            v-if="phase?.subtitle"
            class="banner-text items-center gap-1 text-center text-[10px] font-normal text-white md:inline-flex md:w-auto md:text-xs lg:whitespace-nowrap xl:w-3/5 xl:text-base"
            v-html="replaceWords(phase?.subtitle, 'nuxtlabs')" />
        </div>

        <button
          v-if="phase?.cta && $route?.path !== '/nuxt'"
          class="hidden h-[30px]  items-center justify-center rounded-md bg-bittersweet px-4 text-xs  font-bold text-eerie-black group-hover:bg-white md:inline-flex">
          {{ phase?.cta }}
        </button>

        <div
          v-if="phase?.addendum || phase?.addendumBefore"
          class="flex flex-col items-center gap-0 md:flex-row">
          <p
            v-if="phase?.addendumBefore"
            class="w-[90px] rounded-md text-center text-[9px] font-bold uppercase text-white md:w-auto md:text-xs lg:bg-nuxt-green/20 lg:px-4 lg:py-1"
            v-html="phase?.addendumBefore" />
          <div
            v-if="phase?.addendum"
            class="w-10 text-center text-[8px] font-bold uppercase text-white md:w-auto md:px-4 md:py-1 xl:text-xs"
            style="letter-spacing: 1.2px">
            {{ phase?.addendum }}
          </div>
        </div>

        <div
          v-if="phase?.showCountdown"
          class="flex flex-col items-center lg:flex-row lg:gap-4">
          <span class="text-gradient-primary whitespace-nowrap text-[8px] font-bold uppercase lg:text-[10px] lg:tracking-widest">
            {{ phase?.countdownFooter }}
          </span>
          <AppCountdown
            v-bind="{ remaining: phase?.remaining, daysToHours: phase?.showDaysAsHours }"
            class="dark"
            box-class="text-white lg:scale-[1.2] lg:translate-x-2 p-1 md:!px-0 md:!py-0 lg:bg-nuxt-green/30  lg:!px-2 lg:!py-1"
            :box-style="'background: rgba(0, 220, 130, 0.20);'" />
        </div>
      </div>
    </div>
    <div
      v-if="currentTheme === 'autumn-sales'"
      class="my-auto w-full">
      <div class="mx-auto flex max-w-7xl -translate-x-3 items-center justify-center gap-3 px-4 md:translate-x-0 md:gap-10 lg:gap-16 xl:gap-20">
        <div class="flex items-center gap-2">
          <InlineSvg
            src="/images/autumn-leaf.svg"
            class="size-8 text-nuxt-green"
            alt="Autumn Sales Banner" />
          <p
            class="w-18 text-sm font-black uppercase leading-none text-white md:w-auto md:text-lg [&>span]:text-nuxt-green"
            v-html="phase?.prelude" />
        </div>
        <div class="[&>p]:!leading-1 flex flex-col items-center md:flex-row md:gap-10 lg:gap-16 xl:gap-20">
          <p
            class="text-sm font-normal text-white md:text-lg"
            v-html="phase?.title" />

          <p
            class="text-xs font-bold uppercase tracking-[1.8px] text-nuxt-green md:text-lg"
            v-html="phase?.interlude" />
        </div>

        <div
          v-if="phase?.showCountdown"
          class="flex flex-col items-center md:flex-row md:gap-2">
          <span
            v-if="phase?.id?.includes('EXT')"
            class="whitespace-nowrap text-[8px]  font-bold uppercase text-nuxt-green md:text-[12px] md:tracking-widest xl:mr-9">
            EXTENDED
          </span>
          <span
            class="whitespace-nowrap text-[8px] font-bold uppercase text-white md:text-[10px] md:tracking-widest">
            ends in
          </span>
          <AppCountdown
            v-bind="{
              remaining: phase?.remaining,
              daysToHours: phase?.showDaysAsHours,
            }"
            class="dark"
            box-class="text-white p-1 md:px-2 md:py-1 bg-transparent md:bg-nuxt-green/20"
            box-style="" />
        </div>
      </div>
    </div>
    <button
      class="absolute right-2 top-4 xl:right-3"
      @click="closeBanner">
      <span class="sr-only">close</span><XMarkIcon class="x-5 h-5 text-white" />
    </button>
  </NuxtLink>
</template>

<style scoped>
.banner-text :deep(span) {
  @apply  text-nuxt-green
}
</style>
